(() => {
  if (window.__sofsyAttributionCapture) return;
  window.__sofsyAttributionCapture = 1;

  // Shopify discards the landing query string, so a click identifier that is not
  // deliberately persisted never reaches the order. This is write-once data: a paid
  // click that arrives before it is stored can never be joined to its order, by any
  // later backfill. clarity-tagging.js already reads these params, but only into
  // sessionStorage for Clarity, which never reaches Shopify.
  //
  // gbraid and wbraid are Google's replacements for gclid on iOS and app-to-web
  // journeys where gclid is absent. Capturing only gclid silently loses those.
  // srsltid comes from Merchant Center auto-tagging, mostly on free listing clicks; a
  // paid Shopping click carries gclid (sometimes with srsltid), so gclid's presence is
  // what separates paid from free.
  const CLICK_IDS = [
    "gclid", "gbraid", "wbraid", "dclid", "srsltid",
    "fbclid", "msclkid", "ttclid", "epik", "irclickid",
  ];
  const UTM_KEYS = ["utm_source", "utm_medium", "utm_campaign", "utm_content", "utm_term", "utm_id"];

  // The Google Ads account runs a tracking URL template that appends these on every
  // paid click (verified 2026-09-25 on customer 4270460256). They carry the campaign,
  // ad group, creative, network, target and match type, and they are discarded with
  // the query string exactly as gclid is. gclid can only be resolved back to those
  // dimensions through click_view, which Google caps at 90 days, so past that window
  // the URL is the only surviving record.
  const VALUETRACK_KEYS = [
    "hsa_acc", "hsa_cam", "hsa_grp", "hsa_ad", "hsa_src",
    "hsa_tgt", "hsa_kw", "hsa_mt", "hsa_net", "hsa_ver",
    "gad_source", "gad_campaignid",
  ];

  const TRACKED = CLICK_IDS.concat(UTM_KEYS, VALUETRACK_KEYS);

  const COOKIE = "sofsy_attr";
  // Matches the 90-day click window Google attributes conversions over, so a cookie
  // never expires while Google would still be crediting the click.
  const COOKIE_DAYS = 90;
  const SYNC_FLAG = "sofsy_attr_synced";
  const MAX_VALUE = 240;

  const readCookie = (name) => {
    const match = document.cookie.match(new RegExp("(?:^|; )" + name + "=([^;]*)"));
    if (!match) return null;
    try {
      return JSON.parse(decodeURIComponent(match[1]));
    } catch (_) {
      return null;
    }
  };

  // Browsers silently discard a cookie over ~4KB rather than erroring, which would
  // lose the whole record including last touch. Now that a tagged click can carry
  // twenty-odd parameters across two touches, drop first touch rather than risk that:
  // last touch is what a decision is made on, first touch is context.
  const MAX_COOKIE = 3500;

  const writeCookie = (name, value) => {
    try {
      const expires = new Date(Date.now() + COOKIE_DAYS * 864e5).toUTCString();
      let payload = encodeURIComponent(JSON.stringify(value));
      if (payload.length > MAX_COOKIE && value && value.last) {
        payload = encodeURIComponent(JSON.stringify({ last: value.last }));
      }
      if (payload.length > MAX_COOKIE) return;
      document.cookie =
        name +
        "=" +
        payload +
        "; expires=" + expires +
        "; path=/; SameSite=Lax" +
        (location.protocol === "https:" ? "; Secure" : "");
    } catch (_) {}
  };

  const params = new URLSearchParams(location.search);

  // Values land in the order record and are read by humans and by the warehouse.
  // Trim rather than reject so a malformed param degrades to a short string instead
  // of discarding an otherwise valid click.
  const clean = (value) => String(value).replace(/\s+/g, " ").trim().slice(0, MAX_VALUE);

  const fromUrl = {};
  // A value that is blank once cleaned (`?utm_source=%20`, `?gclid=+`) is no value: it records nothing and is no touch.
  // params.get reads the FIRST occurrence of a key, here and in the touch decision below alike.
  TRACKED.forEach((key) => {
    const value = params.get(key);
    const v = value === null ? "" : clean(value);
    if (v) fromUrl[key] = v;
  });

  // Storefront #72, touch rule "tracked-only" (master, 3 Oct): a NEW touch needs at least one non-empty TRACKED key
  // (a listed utm_*, a listed click id, a listed ValueTrack key), or any hsa_* / gad_* key even when unlisted (Google's
  // tracking template may add more). An unlisted utm_* (utm_referrer, utm_ref, ...) or any other parameter is never
  // a touch and never blanks a set.
  const isTouchKey = (key) => TRACKED.indexOf(key) !== -1 || /^(hsa_|gad_)/.test(key);
  let hasNewAttribution = Object.keys(fromUrl).length > 0;
  params.forEach((_, key) => {
    if (!isTouchKey(key)) return;
    const value = params.get(key);
    if (value !== null && clean(value)) hasNewAttribution = true;
  });
  const stored = readCookie(COOKIE) || {};

  // First touch is written once and never overwritten; last touch moves with every
  // tagged visit. Keeping both costs two extra fields and is the difference between
  // being able to answer "what introduced this customer" and only "what closed them".
  let record = stored;
  if (hasNewAttribution) {
    record = {
      first: stored.first || { ...fromUrl, at: new Date().toISOString(), landing: clean(location.pathname) },
      last: { ...fromUrl, at: new Date().toISOString(), landing: clean(location.pathname) },
    };
    writeCookie(COOKIE, record);
    try {
      sessionStorage.removeItem(SYNC_FLAG);
    } catch (_) {}
  }

  // assets/cart.js asks this, at the moment of each storefront cart write, for attributes to carry, and tells it
  // after a successful one (afterCartWrite, the fallback below).
  let pending = null;
  let checked = false;
  let afterCartWrite = () => {};
  let pendingNow = () => null;
  window.sofsyAttribution = {
    pendingAttributes: () => {
      try {
        const now = pendingNow();
        return now ? { ...now } : null;
      } catch (_) {
        return null;
      }
    },
    afterCartWrite: (cart) => {
      try {
        afterCartWrite(cart);
      } catch (_) {}
    },
  };

  if (!record || !record.last) return;

  // Underscore-prefixed cart attributes follow the convention already used by the
  // gift mechanism in this theme: retained on the order and retrievable through
  // customAttributes, but not surfaced to the customer.
  //
  // Storefront #72: /cart/update.js MERGES attributes, so writing only the keys of the last touch kept the earlier
  // touch's keys beside them (a later fbclid-only click kept the earlier visit's _utm_*). The cart mirrors the last
  // touch exactly instead: EVERY tracked key is written, blank when the last touch did not carry it (Shopify deletes a
  // blank attribute). Never a mix of two clicks. First-touch fields are not part of that set and keep their meaning.
  // Values come from a cookie anyone can edit: only a string or a number counts, always as a cleaned string of at
  // most MAX_VALUE characters, so nothing else can reach a cart write's body.
  const text = (value) => (typeof value === "string" || typeof value === "number" ? clean(value) : "");
  const desiredFrom = (rec) => {
    const out = {};
    TRACKED.forEach((key) => {
      out["_" + key] = text(rec.last[key]);
    });
    out._attr_landing = text(rec.last.landing);
    out._attr_at = text(rec.last.at);
    const firstAt = rec.first ? text(rec.first.at) : "";
    if (firstAt && firstAt !== out._attr_at) {
      out._attr_first_at = firstAt;
      const firstSource = text(rec.first.utm_source) || CLICK_IDS.find((k) => text(rec.first[k])) || "";
      if (firstSource) out._attr_first_source = firstSource;
    }
    return out;
  };
  const desired = desiredFrom(record);
  // The touch as the sofsy_attr cookie holds it NOW, which another tab may have moved on since this page loaded (a
  // stale tab must never write an older touch over a newer one). This page's own reading when the cookie is
  // unreadable, or holds an OLDER touch than this page saw (the new touch's cookie was too big to write, so the
  // cookie still has the previous one).
  const desiredNow = () => {
    const latest = readCookie(COOKIE);
    if (!latest || !latest.last || typeof latest.last !== "object") return desired;
    const latestAt = text(latest.last.at);
    const ownAt = text(record.last.at);
    return !ownAt || (latestAt && latestAt >= ownAt) ? desiredFrom(latest) : desired;
  };

  let alreadySynced = false;
  try {
    alreadySynced = sessionStorage.getItem(SYNC_FLAG) === "1";
  } catch (_) {}
  if (alreadySynced) return;

  // Page-load race: for a visitor with no cart yet, a page-load /cart/update.js CREATES her cart, and its cart
  // cookie only exists once that response is back. An add she sends before then goes out without the cookie, so
  // Shopify opens a second cart for it; whichever Set-Cookie lands last wins, and the bag can come back empty or
  // without the attribution. So: when the page was rendered with no items in the cart (or, when that cannot be
  // read, with no `cart` cookie), nothing is written here. The attributes ride on her first storefront write
  // (assets/cart.js storefrontAttributesNow -> attributionAttributesNow), which is the request that creates the cart.
  // An empty cart cannot reach checkout, so nothing is lost by waiting; a cookie for a cart Shopify has since dropped
  // would race exactly the same way. With items in the cart, the cart and its cookie already exist, every request
  // goes to that one cart, and the page-load write stays: a returning shopper who clicks a new ad and goes straight
  // to checkout is still credited to that click.
  const renderedItemCount = () => {
    const sources = [["bag-state", "item_count"], ["clarity-ctx", "cartCount"]];
    for (let i = 0; i < sources.length; i += 1) {
      try {
        const element = document.getElementById(sources[i][0]);
        if (!element) continue;
        const count = Number(JSON.parse(element.textContent)[sources[i][1]]);
        if (Number.isFinite(count)) return count;
      } catch (_) {}
    }
    return null;
  };
  const count = renderedItemCount();
  const hasCart = count !== null ? count > 0 : /(?:^|; )cart=[^;]+/.test(document.cookie);
  // A blank in `desired` means "not there": Shopify drops a blank attribute rather than storing it.
  const differing = (attributes, wanted = desired) => {
    const out = {};
    Object.keys(wanted).forEach((key) => {
      const value = (attributes || {})[key];
      if ((value === undefined || value === null ? "" : String(value)) !== wanted[key]) out[key] = wanted[key];
    });
    return out;
  };
  const matches = (attributes) => Object.keys(differing(attributes)).length === 0;
  const markSynced = () => {
    try {
      sessionStorage.setItem(SYNC_FLAG, "1");
    } catch (_) {}
  };

  if (!hasCart) {
    pending = desired;
    pendingNow = () => (pending ? desiredNow() : null);
    // Fallback, whatever Shopify does with `attributes` on /cart/add.js: after the FIRST successful storefront write
    // that carried them, the cart is checked once, from the write's own answer when it is a whole cart
    // (/cart/update.js), else with one GET /cart.js (/cart/add.js answers only the lines). Any key missing or
    // different -> ONE /cart/update.js with exactly those keys; her cart cookie exists by then, so it cannot race.
    // Then the pending set is cleared, either way, success or failure: never more than one fallback per touch, never
    // a retry, and nothing here can touch the add or the bag (it runs after the write has settled, errors swallowed).
    afterCartWrite = (cart) => {
      if (checked || !pending) return;
      checked = true;
      const carried = pending;
      const wanted = desiredNow();
      const read =
        cart && cart.attributes && typeof cart.attributes === "object"
          ? Promise.resolve(cart)
          : fetch("/cart.js", { headers: { Accept: "application/json" }, credentials: "same-origin", cache: "no-store" })
              .then((response) => (response.ok ? response.json() : null));
      read
        .then((current) => {
          if (!current || !current.attributes) return null;
          const missing = differing(current.attributes, wanted);
          if (!Object.keys(missing).length) {
            markSynced();
            return null;
          }
          return fetch("/cart/update.js", {
            method: "POST",
            headers: { "Content-Type": "application/json", Accept: "application/json" },
            credentials: "same-origin",
            body: JSON.stringify({ attributes: missing }),
          }).then((response) => {
            if (response && response.ok) markSynced();
          });
        })
        .catch(() => {})
        .then(() => {
          if (pending === carried) pending = null;
        });
    };
    return;
  }

  // Read before writing. An unconditional POST on every page would rewrite the cart
  // for every paid visitor on every pageview; the GET is small and usually settles it.
  fetch("/cart.js", { headers: { Accept: "application/json" }, credentials: "same-origin" })
    .then((response) => (response.ok ? response.json() : null))
    .then((cart) => {
      if (cart && matches(cart.attributes)) {
        try {
          sessionStorage.setItem(SYNC_FLAG, "1");
        } catch (_) {}
        return null;
      }
      return fetch("/cart/update.js", {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        credentials: "same-origin",
        body: JSON.stringify({ attributes: desired }),
      });
    })
    .then((response) => {
      if (response && response.ok) {
        try {
          sessionStorage.setItem(SYNC_FLAG, "1");
        } catch (_) {}
      }
    })
    .catch(() => {});
})();
